import type { CommonError } from '../../common/types/errors.js';
import type { ItemFilter } from '../../common/types/subscription.js';
import type { GenericError } from '../../generated/api/models.js';
import type { ActionWithId } from '../common/store.js';
import type { Action } from 'redux';

export enum TypeKeys {
  // Direct analytic events without any other interaction events
  APPLY_ANALYTICS_EVENT = 'APPLY_ANALYTICS_EVENT',
  APPLY_WIZARD_EVENT = 'APPLY_WIZARD_EVENT',

  // Myself
  POLL_FOR_MYSELF_CHANGES = 'POLL_FOR_MYSELF_CHANGES',
  POLL_FOR_MYSELF_CHANGES_FAILED = 'POLL_FOR_MYSELF_CHANGES_FAILED',
  POLL_FOR_MYSELF_CHANGES_FULFILLED = 'POLL_FOR_MYSELF_CHANGES_FULFILLED',
  STOP_POLL_FOR_MYSELF_CHANGES = 'STOP_POLL_FOR_MYSELF_CHANGES',

  // Billing accounts
  LOAD_BILLING_ACCOUNTS = 'LOAD_BILLING_ACCOUNTS',
  LOAD_BILLING_ACCOUNTS_FAILED = 'LOAD_BILLING_ACCOUNTS_FAILED',
  LOAD_BILLING_ACCOUNTS_FULFILLED = 'LOAD_BILLING_ACCOUNTS_FULFILLED',
  UPSERT_BILLING_ACCOUNT_SCHEDULED_CHANGE_FULFILLED = 'UPSERT_BILLING_ACCOUNT_SCHEDULED_CHANGE_FULFILLED',
  UPSERT_BILLING_ACCOUNT_SCHEDULED_CHANGE_FAILED = 'UPSERT_BILLING_ACCOUNT_SCHEDULED_CHANGE_FAILED',
  PREPARE_BILLING_ACCOUNT_EDIT = 'PREPARE_BILLING_ACCOUNT_EDIT',

  // Commercial products
  LOAD_ONLINE_MODEL = 'LOAD_ONLINE_MODEL',
  LOAD_ONLINE_MODEL_FAILED = 'LOAD_ONLINE_MODEL_FAILED',
  LOAD_ONLINE_MODEL_FULFILLED = 'LOAD_ONLINE_MODEL_FULFILLED',
  LOAD_ONLINE_MODELS = 'LOAD_ONLINE_MODELS',
  LOAD_ONLINE_MODELS_FAILED = 'LOAD_ONLINE_MODELS_FAILED',
  LOAD_ONLINE_MODELS_FULFILLED = 'LOAD_ONLINE_MODELS_FULFILLED',
  CLEAR_CAMPAIGNS = 'CLEAR_CAMPAIGNS',
  GET_CAMPAIGNS = 'GET_CAMPAIGNS',
  GET_CAMPAIGNS_FAILED = 'GET_CAMPAIGNS_FAILED',
  GET_CAMPAIGNS_FULFILLED = 'GET_CAMPAIGNS_FULFILLED',

  // Contacts
  LOAD_CONTACTS = 'LOAD_CONTACTS',
  LOAD_CONTACTS_FAILED = 'LOAD_CONTACTS_FAILED',
  LOAD_CONTACTS_FULFILLED = 'LOAD_CONTACTS_FULFILLED',
  UPSERT_CONTACT = 'UPSERT_CONTACT',
  UPSERT_CONTACT_FAILED = 'UPSERT_CONTACT_FAILED',
  UPSERT_CONTACT_FULFILLED = 'UPSERT_CONTACT_FULFILLED',
  // Company info
  LOAD_COMPANY_INFO = 'LOAD_COMPANY_INFO',
  LOAD_COMPANY_INFO_FULFILLED = 'LOAD_COMPANY_INFO_FULFILLED',
  LOAD_COMPANY_INFO_FAILED = 'LOAD_COMPANY_INFO_FAILED',

  // Change subscription user info - contact creation (used only by the "change subscription user info" flow)
  CREATE_CONTACT_FOR_SUBSCRIPTION_USER_INFO = 'CREATE_CONTACT_FOR_SUBSCRIPTION_USER_INFO',
  CREATE_CONTACT_FOR_SUBSCRIPTION_USER_INFO_FAILED = 'CREATE_CONTACT_FOR_SUBSCRIPTION_USER_INFO_FAILED',
  CREATE_CONTACT_FOR_SUBSCRIPTION_USER_INFO_FULFILLED = 'CREATE_CONTACT_FOR_SUBSCRIPTION_USER_INFO_FULFILLED',

  // Subscription Actions
  LOAD_SUBSCRIPTION_ACTIONS = 'LOAD_SUBSCRIPTION_ACTIONS',
  LOAD_SUBSCRIPTION_ACTIONS_FAILED = 'LOAD_SUBSCRIPTION_ACTIONS_FAILED',
  LOAD_SUBSCRIPTION_ACTIONS_FULFILLED = 'LOAD_SUBSCRIPTION_ACTIONS_FULFILLED',

  // General
  SHOW_DIALOG = 'SHOW_DIALOG',
  CLOSE_DIALOG = 'CLOSE_DIALOG',
  SET_DIALOG_SUBMIT_FAILED = 'SET_DIALOG_SUBMIT_FAILED',
  OTHER_ACTION = '__any_other_action_type__',

  // Invoices
  INITIALIZE_PAYMENT = 'INITIALIZE_PAYMENT',
  INITIALIZE_PAYMENT_FULFILLED = 'INITIALIZE_PAYMENT_FULFILLED',
  INITIALIZE_PAYMENT_FAILED = 'INITIALIZE_PAYMENT_FAILED',
  FINALIZE_PAYMENT = 'FINALIZE_PAYMENT',
  FINALIZE_PAYMENT_FULFILLED = 'FINALIZE_PAYMENT_FULFILLED',
  FINALIZE_PAYMENT_FAILED = 'FINALIZE_PAYMENT_FAILED',

  // Layout
  FOOTER_SET_VISIBILITY = 'FOOTER_SET_VISIBILITY',
  SMALL_HEADER_SET_VISIBILITY = 'SMALL_HEADER_SET_VISIBILITY',

  // Online order
  SUBMIT_ONLINE_ORDER = 'SUBMIT_ONLINE_ORDER',
  SUBMIT_ONLINE_ORDER_FULFILLED = 'SUBMIT_ONLINE_ORDER_FULLFILLED',
  SUBMIT_ONLINE_ORDER_FAILED = 'SUBMIT_ONLINE_ORDER_FAILED',
  POLL_FOR_CREDIT_CHECK = 'POLL_FOR_CREDIT_CHECK',
  POLL_FOR_CREDIT_CHECK_FAILED = 'POLL_FOR_CREDIT_CHECK_FAILED',
  POLL_FOR_CREDIT_CHECK_V2_FULFILLED = 'POLL_FOR_CREDIT_CHECK_V2_FULFILLED',

  SEARCH_PICKUP_POINT = 'SEARCH_PICKUP_POINT',
  SEARCH_PICKUP_POINT_FULFILLED = 'SEARCH_PICKUP_POINT_FULFILLED',
  SEARCH_PICKUP_POINT_FAILED = 'SEARCH_PICKUP_POINT_FAILED',

  // Omalasku
  OMALASKU_ORDER_SUBMITTED = 'OMALASKU_ORDER_SUBMITTED',
  OMALASKU_ORDER_FAILED_TO_SUBMIT = 'OMALASKU_ORDER_FAILED_TO_SUBMIT',

  // Site actions (should actually not be under selfservice but are needed also for selfservice to work)
  // Forces the browser to load the given URL instead of application preforming internal redirect to another component.
  HARD_REDIRECT = 'HARD_REDIRECT',

  // Subscriptions
  LOAD_SUBSCRIPTIONS = 'LOAD_SUBSCRIPTIONS',
  LOAD_SUBSCRIPTIONS_FAILED = 'LOAD_SUBSCRIPTIONS_FAILED',
  LOAD_SUBSCRIPTIONS_FULFILLED = 'LOAD_SUBSCRIPTIONS_FULFILLED',

  // Subscription actions
  TERMINATE_SUBSCRIPTION = 'TERMINATE_SUBSCRIPTION',
  TERMINATE_SUBSCRIPTION_FAILED = 'TERMINATE_SUBSCRIPTION_FAILED',
  TERMINATE_SUBSCRIPTION_FULFILLED = 'TERMINATE_SUBSCRIPTION_FULFILLED',
  SHOW_SUSPEND_SUBSCRIPTION_OPTIONS = 'SHOW_SUSPEND_SUBSCRIPTION_OPTIONS',
  CLOSE_SUSPEND_SUBSCRIPTION_OPTIONS = 'CLOSE_SUSPEND_SUBSCRIPTION_OPTIONS',
  SHOW_TERMINATE_SUBSCRIPTION_FORM = 'SHOW_TERMINATE_SUBSCRIPTION_FORM',
  CLOSE_TERMINATE_SUBSCRIPTION_FORM = 'CLOSE_TERMINATE_SUBSCRIPTION_FORM',
  SUSPEND_SUBSCRIPTION = 'SUSPEND_SUBSCRIPTION',
  SUSPEND_SUBSCRIPTION_FULFILLED = 'SUSPEND_SUBSCRIPTION_FULFILLED',
  SUSPEND_SUBSCRIPTION_FAILED = 'SUSPEND_SUBSCRIPTION_FAILED',
  RESUME_SUBSCRIPTION = 'RESUME_SUBSCRIPTION',
  RESUME_SUBSCRIPTION_FULFILLED = 'RESUME_SUBSCRIPTION_FULFILLED',
  RESUME_SUBSCRIPTION_FAILED = 'RESUME_SUBSCRIPTION_FAILED',
  EDIT_SUBSCRIPTION_SECTION = 'EDIT_SUBSCRIPTION_SECTION',
  CHANGE_SUBSCRIPTION_USER_INFO = 'CHANGE_SUBSCRIPTION_USER_INFO',
  CHANGE_SUBSCRIPTION_USER_INFO_FAILED = 'CHANGE_SUBSCRIPTION_USER_INFO_FAILED',
  CHANGE_SUBSCRIPTION_USER_INFO_WITH_DUPLICATE_FAILED = 'CHANGE_SUBSCRIPTION_USER_INFO_WITH_DUPLICATE_FAILED',
  CHANGE_SUBSCRIPTION_USER_INFO_FULFILLED = 'CHANGE_SUBSCRIPTION_USER_INFO_FULFILLED',
  USE_DUPLICATE_CONTACT = 'USE_DUPLICATE_CONTACT',
  CHANGE_SUBSCRIPTION_PBX_CONFIGURATION = 'CHANGE_SUBSCRIPTION_PBX_CONFIGURATION',
  CHANGE_SUBSCRIPTION_PBX_CONFIGURATION_FAILED = 'CHANGE_SUBSCRIPTION_PBX_CONFIGURATION_FAILED',
  CHANGE_SUBSCRIPTION_PBX_CONFIGURATION_FULFILLED = 'CHANGE_SUBSCRIPTION_PBX_CONFIGURATION_FULFILLED',
  CHANGE_ADDONS = 'CHANGE_ADDONS',
  CHANGE_ADDONS_FAILED = 'CHANGE_ADDONS_FAILED',
  CHANGE_ADDONS_FULFILLED = 'CHANGE_ADDONS_FULFILLED',
  BARRINGS_VALIDATION_FAILED = 'BARRINGS_VALIDATION_FAILED',
  POST_EPP_REDEEM_TERMINATE = 'POST_EPP_REDEEM_TERMINATE',
  POST_EPP_REDEEM_TERMINATE_FULFILLED = 'POST_EPP_REDEEM_TERMINATE_FULFILLED',
  POST_EPP_REDEEM_TERMINATE_FAILED = 'POST_EPP_REDEEM_TERMINATE_FAILED',

  // Support cases
  ADD_SUPPORT_CASE = 'ADD_SUPPORT_CASE',
  ADD_SUPPORT_CASE_FULFILLED = 'ADD_SUPPORT_CASE_FULFILLED',
  ADD_SUPPORT_CASE_FAILED = 'ADD_SUPPORT_CASE_FAILED',
  SET_CASE_SENT_STATUS = 'SET_CASE_SENT_STATUS',

  // Employee
  GET_EMPLOYEE_SUBSCRIPTIONS = 'GET_EMPLOYEE_SUBSCRIPTIONS',
  GET_EMPLOYEE_SUBSCRIPTIONS_FULLFILLED = 'GET_EMPLOYEE_SUBSCRIPTIONS_FULLFILLED',
  GET_EMPLOYEE_SUBSCRIPTIONS_FAILED = 'GET_EMPLOYEE_SUBSCRIPTIONS_FAILED',
  CHECK_VALID_EMPLOYEE_SUBSCRIPTION = 'CHECK_VALID_EMPLOYEE_SUBSCRIPTION',
  GET_SUBSCRIPTION_LOCAL_USAGE = 'GET_SUBSCRIPTION_LOCAL_USAGE',
  GET_SUBSCRIPTION_LOCAL_USAGE_FULFILLED = 'GET_SUBSCRIPTION_LOCAL_USAGE_FULFILLED',
  GET_SUBSCRIPTION_LOCAL_USAGE_FAILED = 'GET_SUBSCRIPTION_LOCAL_USAGE_FAILED',
  GET_SUBSCRIPTION_ROAMING_USAGE = 'GET_SUBSCRIPTION_ROAMING_USAGE',
  GET_SUBSCRIPTION_ROAMING_USAGE_FULFILLED = 'GET_SUBSCRIPTION_ROAMING_USAGE_FULFILLED',
  GET_SUBSCRIPTION_ROAMING_USAGE_FAILED = 'GET_SUBSCRIPTION_ROAMING_USAGE_FAILED',
  RESET_ERRORS = 'RESET_ERRORS',
  RESEND_EMAIL_VERIFICATION_LINK = 'RESEND_EMAIL_VERIFICATION_LINK',
  RESEND_EMAIL_VERIFICATION_LINK_FULFILLED = 'RESEND_EMAIL_VERIFICATION_LINK_FULFILLED',
  RESEND_EMAIL_VERIFICATION_LINK_FAILED = 'RESEND_EMAIL_VERIFICATION_LINK_FAILED',
  // Onboarding Employee
  INITIATE_EMPLOYEE_ONBOARDING = 'INITIATE_EMPLOYEE_ONBOARDING',
  INITIATE_EMPLOYEE_ONBOARDING_FULFILLED = 'INITIATE_EMPLOYEE_ONBOARDING_FULFILLED',
  INITIATE_EMPLOYEE_ONBOARDING_FAILED = 'INITIATE_EMPLOYEE_ONBOARDING_FAILED',
  INITIATE_EMPLOYEE_ONBOARDING_RESEND_FAILED = 'INITIATE_EMPLOYEE_ONBOARDING_RESEND_FAILED',

  // Resources
  LOAD_NUMBER_RANGE = 'LOAD_NUMBER_RANGE',
  LOAD_NUMBER_RANGE_FAILED = 'LOAD_NUMBER_RANGE_FAILED',
  LOAD_NUMBER_RANGE_FULFILLED = 'LOAD_NUMBER_RANGE_FULFILLED',
  SEARCH_PUBLIC_PAGES = 'SEARCH_PUBLIC_PAGES',
  SEARCH_PUBLIC_PAGES_FULFILLED = 'SEARCH_PUBLIC_PAGES_FULFILLED',
  SEARCH_PUBLIC_PAGES_FAILED = 'SEARCH_PUBLIC_PAGES_FAILED',
  RESET_SEARCH_PUBLIC_PAGES_RESULT = 'RESET_SEARCH_PUBLIC_PAGES_RESULT',

  // DEVICE CHECKOUT
  ADD_TO_CART = 'ADD_TO_CART',
  ADD_CART_EVENT_UPDATE_TO_ANALYTICS = 'ADD_CART_EVENT_UPDATE_TO_ANALYTICS',
  DUPLICATE_CONTACTS_CHECK = 'DUPLICATE_CONTACTS_CHECK',
  DUPLICATE_CONTACTS_CHECK_FAILED = 'DUPLICATE_CONTACTS_CHECK_FAILED',
  DUPLICATE_CONTACTS_CHECK_FULFILLED = 'DUPLICATE_CONTACTS_CHECK_FULFILLED',
  DUPLICATE_CONTACTS_CHECK_SUBMIT = 'DUPLICATE_CONTACTS_CHECK_SUBMIT',
  UPDATE_CART_ITEM = 'UPDATE_CART_ITEM',
  UPDATE_CART_ITEM_PAYMENT = 'UPDATE_CART_ITEM_PAYMENT',
  POST_PROCESS_CART_ITEMS = 'POST_PROCESS_CART_ITEMS',
  PROCESS_CART_ITEMS = 'PROCESS_CART_ITEMS',
  PROCESS_DELIVERY_DETAILS = 'PROCESS_DELIVERY_DETAILS',
  PROCESS_ADDITIONAL_PARAMETER_IN_CART_ITEMS = 'PROCESS_ADDITIONAL_PARAMETER_IN_CART_ITEMS',
  UPDATE_SELECTED_BA_ID = 'UPDATE_SELECTED_BA_ID',
  RESET_MOVE_TO_NEXT_STEP = 'RESET_MOVE_TO_NEXT_STEP',
  CLEAR_CART_ITEMS = 'CLEAR_CART_ITEMS',
  CLEAR_DELIVERY_DETAILS = 'CLEAR_DELIVERY_DETAILS',
  UPSERT_PERSON_BILLING_ACCOUNT = 'UPSERT_PERSON_BILLING_ACCOUNT',
  UPSERT_PERSON_BILLING_ACCOUNT_FULFILLED = 'UPSERT_PERSON_BILLING_ACCOUNT_FULFILLED',
  UPSERT_PERSON_BILLING_ACCOUNT_FAILED = 'UPSERT_PERSON_BILLING_ACCOUNT_FAILED',
  SET_CART_UPDATED = 'SET_CART_UPDATED',
  GET_DELIVERY_METHODS = 'GET_DELIVERY_METHODS',
  GET_DELIVERY_METHODS_FULFILLED = 'GET_DELIVERY_METHODS_FULFILLED',
  GET_DELIVERY_METHODS_FAILED = 'GET_DELIVERY_METHODS_FAILED',

  // Virtual Catalogs - replaces product catalog
  LOAD_VIRTUAL_CATALOGS = 'LOAD_VIRTUAL_CATALOGS',
  LOAD_VIRTUAL_CATALOGS_FULFILLED = 'LOAD_VIRTUAL_CATALOGS_FULFILLED',
  LOAD_VIRTUAL_CATALOGS_FAILED = 'LOAD_VIRTUAL_CATALOGS_FAILED',
  SET_SELECTED_VIRTUAL_CATALOG = 'SET_SELECTED_VIRTUAL_CATALOG',

  // Address
  SEARCH_ADDRESS = 'SEARCH_ADDRESS',
  SEARCH_ADDRESS_FULFILLED = 'SEARCH_ADDRESS_FULFILLED',
  SEARCH_ADDRESS_FAILED = 'SEARCH_ADDRESS_FAILED',
  RESET_ADDRESS_SEARCH_RESULT = 'RESET_ADDRESS_SEARCH_RESULT',

  // Google Ecommerce Events
  GOOGLE_ECOMMERCE_EVENT = 'GOOGLE_ECOMMERCE_EVENT',
  GOOGLE_ECOMMERCE_ADD_PAYMENT_INFO = 'GOOGLE_ECOMMERCE_ADD_PAYMENT_INFO',
  GOOGLE_ECOMMERCE_ADD_SHIPPING_INFO = 'GOOGLE_ECOMMERCE_ADD_SHIPPING_INFO',
  GOOGLE_ECOMMERCE_ADD_TO_CART = 'GOOGLE_ECOMMERCE_ADD_TO_CART',
  GOOGLE_ECOMMERCE_CHECKOUT = 'GOOGLE_ECOMMERCE_CHECKOUT',
  GOOGLE_ECOMMERCE_PRODUCT_CLICK = 'GOOGLE_ECOMMERCE_PRODUCT_CLICK',
  GOOGLE_ECOMMERCE_PRODUCT_DETAIL = 'GOOGLE_ECOMMERCE_PRODUCT_DETAIL',
  GOOGLE_ECOMMERCE_PURCHASE = 'GOOGLE_ECOMMERCE_PURCHASE',
  GOOGLE_ECOMMERCE_REMOVE_FROM_CART = 'GOOGLE_ECOMMERCE_REMOVE_FROM_CART',

  // Used to save shopping-cart for post processing for reminders
  NOTIFY_SHOPPING_CART_CHANGE = 'NOTIFY_SHOPPING_CART_CHANGE',
  SUBMIT_SHOPPING_CART_CHANGE = 'SUBMIT_SHOPPING_CART_CHANGE',
  SUBMIT_SHOPPING_CART_CHANGE_FULFILLED = 'SUBMIT_SHOPPING_CART_CHANGE_FULFILLED',
  SUBMIT_SHOPPING_CART_CHANGE_FAILED = 'SUBMIT_SHOPPING_CART_CHANGE_FAILED',

  // Auxiliary eSim
  AUXILIARY_ESIM_ORDER_SET_PHASE = 'AUXILIARY_ESIM_ORDER_SET_PHASE',
  AUXILIARY_ESIM_ORDER_GET_SUBSCRIPTION_FULFILLED = 'AUXILIARY_ESIM_ORDER_GET_SUBSCRIPTION_FULFILLED',
  AUXILIARY_ESIM_ORDER_GET_SUBSCRIPTION_FAILED = 'AUXILIARY_ESIM_ORDER_GET_SUBSCRIPTION_FAILED',
  AUXILIARY_ESIM_ORDER_SEND = 'AUXILIARY_ESIM_ORDER_SEND',
  AUXILIARY_ESIM_ORDER_SEND_FAILED = 'AUXILIARY_ESIM_ORDER_SEND_FAILED',
  AUXILIARY_ESIM_ORDER_SEND_FULFILLED = 'AUXILIARY_ESIM_ORDER_SEND_FULFILLED',
  AUXILIARY_ESIM_ORDER_VALIDATE = 'AUXILIARY_ESIM_ORDER_VALIDATE',
  AUXILIARY_ESIM_ORDER_VALIDATE_FULFILLED = 'AUXILIARY_ESIM_ORDER_VALIDATE_FULFILLED',
  AUXILIARY_ESIM_ORDER_VALIDATE_FAILED = 'AUXILIARY_ESIM_ORDER_VALIDATE_FAILED',

  // Punchout
  SUBMIT_PUNCHOUT_ORDER = 'SUBMIT_PUNCHOUT_ORDER',
  SUBMIT_PUNCHOUT_ORDER_FULFILLED = 'SUBMIT_PUNCHOUT_ORDER_FULLFILLED',
  SUBMIT_PUNCHOUT_ORDER_FAILED = 'SUBMIT_PUNCHOUT_ORDER_FAILED',

  // Authentication side effects
  CLEAR_STATE = 'CLEAR_STATE',
  CLEAR_EMPLOYEE_SUBS = 'CLEAR_EMPLOYEE_SUBS',
  UPDATE_PENDING_SUBSCRIPTION_ACTIONS = 'UPDATE_PENDING_SUBSCRIPTION_ACTIONS',
  LOAD_AUXILIARY_ESIM_SUBSCRIPTION = 'LOAD_AUXILIARY_ESIM_SUBSCRIPTION',
  MOVE_CHECKOUT_TO_NEXT_STEP = 'MOVE_CHECKOUT_TO_NEXT_STEP',

  // Session
  SESSION_EXPIRED = 'SESSION_EXPIRED',
}

export interface OtherAction {
  type: TypeKeys.OTHER_ACTION;
}

export interface CrudAction<T extends TypeKeys> extends ActionWithId {
  type: T;
}

export interface DisplayItemsLoadAction<T extends TypeKeys, U> extends CrudAction<T> {
  displayId?: string;
  filter?: ItemFilter<U>;
  limit?: number;
  search?: string;
  reporting?: boolean;
  status?: string;
}

export interface ErrorAction<T extends TypeKeys> extends Action {
  errors?: CommonError[];
  params?: { [s: string]: string };
  type: T;
}

export type ErrorActionCreator<T extends TypeKeys> = (
  message: string,
  status: number,
  errors?: GenericError[],
  params?: { [s: string]: string }
) => ErrorAction<T>;

export const generateActionId = (): number => {
  if (typeof performance !== 'undefined') {
    return performance.now();
  } else {
    return Date.now();
  }
};
